import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { clampView, DOUBLE_TAP_SCALE, RESET, zoomAt } from './zoom';
import { ViewerButtons, ViewerButton, ViewerImage, ViewerStage } from './styled';

/** Two taps this close together (ms) are a double tap. */
const DOUBLE_TAP_MS = 300;
/** Zoom per button press, and per notch of a mouse wheel. */
const STEP = 1.6;
const WHEEL_STEP = 1.0025;

/**
 * A rulebook image full screen, to read and zoom. Pinch, Ctrl+scroll or trackpad pinch, a double tap, or the
 * buttons zoom; dragging moves around a zoomed image. Escape, the close button, or a tap off the image closes it.
 */
const ImageViewer = ({ src, alt, onClose }) => {
  const [view, setView] = useState(RESET);
  const stage = useRef(null);
  const image = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);

  const sizes = useCallback(() => {
    const box = stage.current.getBoundingClientRect();
    return {
      stage: { width: box.width, height: box.height },
      image: { width: image.current.offsetWidth, height: image.current.offsetHeight },
      center: { x: box.left + box.width / 2, y: box.top + box.height / 2 },
    };
  }, []);

  const zoomTo = useCallback((factor, clientX, clientY) => {
    const { stage: s, image: i, center } = sizes();
    const point = clientX === undefined ? { x: 0, y: 0 } : { x: clientX - center.x, y: clientY - center.y };
    setView(current => zoomAt(current, factor, point, i, s));
  }, [sizes]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === '+' || event.key === '=') zoomTo(STEP);
      if (event.key === '-') zoomTo(1 / STEP);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, zoomTo]);

  // React's wheel listener is passive, so it can't stop the page zooming; attach one that can.
  useLayoutEffect(() => {
    const element = stage.current;
    const onWheel = (event) => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        zoomTo(WHEEL_STEP ** -event.deltaY, event.clientX, event.clientY);
      } else {
        const { stage: s, image: i } = sizes();
        setView(current => clampView({ ...current, x: current.x - event.deltaX, y: current.y - event.deltaY }, i, s));
      }
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [zoomTo, sizes]);

  // The stage covers the whole screen, so it sees every pointer without capturing them, and event.target still
  // tells a tap on the image from a tap beside it.
  const onPointerDown = (event) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    gesture.current = { view, moved: false, start: [...pointers.current.values()].map(p => ({ ...p })) };
  };

  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) {
      return;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const now = [...pointers.current.values()];
    const { start, view: from } = gesture.current;
    const { stage: s, image: i, center } = sizes();

    if (now.length >= 2 && start.length >= 2) {
      const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (now[0].x + now[1].x) / 2 - center.x, y: (now[0].y + now[1].y) / 2 - center.y };
      setView(zoomAt(from, distance(now[0], now[1]) / distance(start[0], start[1]), mid, i, s));
      gesture.current.moved = true;
    } else if (now.length === 1) {
      const dx = now[0].x - start[0].x;
      const dy = now[0].y - start[0].y;
      if (Math.hypot(dx, dy) > 4) {
        gesture.current.moved = true;
      }
      setView(clampView({ ...from, x: from.x + dx, y: from.y + dy }, i, s));
    }
  };

  const onPointerUp = (event) => {
    pointers.current.delete(event.pointerId);
    const finished = gesture.current;
    // Lifting one finger of a pinch carries on as a drag from where things are now.
    gesture.current = pointers.current.size
      ? { view, moved: true, start: [...pointers.current.values()].map(p => ({ ...p })) }
      : null;
    if (!finished || finished.moved || pointers.current.size) {
      return;
    }
    const tappedImage = event.target === image.current;
    if (event.timeStamp - lastTap.current < DOUBLE_TAP_MS && tappedImage) {
      lastTap.current = 0;
      if (view.scale > 1) {
        setView(RESET);
      } else {
        zoomTo(DOUBLE_TAP_SCALE, event.clientX, event.clientY);
      }
    } else if (!tappedImage && view.scale === 1) {
      onClose();
    } else {
      lastTap.current = event.timeStamp;
    }
  };

  return createPortal(
    <ViewerStage
      ref={stage}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <ViewerImage
        ref={image}
        src={src}
        alt={alt}
        draggable={false}
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        $zoomed={view.scale > 1}
      />
      <ViewerButtons onPointerDown={event => event.stopPropagation()}>
        <ViewerButton onClick={() => zoomTo(1 / STEP)} disabled={view.scale <= 1} aria-label="Zoom out">−</ViewerButton>
        <ViewerButton onClick={() => zoomTo(STEP)} disabled={view.scale >= 5} aria-label="Zoom in">+</ViewerButton>
        <ViewerButton onClick={onClose} aria-label="Close">×</ViewerButton>
      </ViewerButtons>
    </ViewerStage>,
    document.body,
  );
};

ImageViewer.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ImageViewer;
